import { compact } from "lodash";
import { useCallback } from "react";
import {
  ChatbotRoleEnum,
  GetAskAiChatSessionQueryQuery,
  GetAskAiChatSessionQueryQueryVariables,
} from "types/graphql-schema";

import getAskAIChatSessionQuery, {
  AskAiChatMessageFragment,
} from "@apps/ask-ai/graphql/get-ask-ai-session-query";
import { createChatMessageNode } from "@apps/ask-ai/helpers";
import cache from "@cache/cache";
import usePusher from "@components/use-pusher/use-pusher";

export default function PusherChatMessageDelta({
  channelName,
}: {
  channelName: string;
}) {
  const handleChatMessageDelta = useCallback(
    ({
      chat_session_id,
      message_id,
      delta,
    }: {
      chat_session_id: number;
      message_id: string;
      delta: string;
    }) => {
      const chatMessageCacheId = cache.identify({
        __typename: "ChatMessageNode",
        id: message_id,
      });
      const chatMessageCache = cache.readFragment({
        id: chatMessageCacheId,
        fragmentName: "AskAiChatMessageFragment",
        fragment: AskAiChatMessageFragment,
      });

      if (chatMessageCache) {
        cache.updateFragment(
          {
            id: chatMessageCacheId,
            fragmentName: "AskAiChatMessageFragment",
            fragment: AskAiChatMessageFragment,
          },
          (data) => ({
            ...data,
            isStreaming: true,
            content: data.content + delta,
          })
        );
      } else {
        cache.updateQuery<
          GetAskAiChatSessionQueryQuery,
          GetAskAiChatSessionQueryQueryVariables
        >(
          {
            query: getAskAIChatSessionQuery,
            variables: { chatSessionId: chat_session_id },
          },
          (data) => {
            if (!data?.chatSession) return data;
            const chatSession = data.chatSession;
            const updatedMessageNode = createChatMessageNode({
              id: message_id,
              content: delta,
              isStreaming: true,
              role: ChatbotRoleEnum.Assistant,
            });
            const updatedData: GetAskAiChatSessionQueryQuery = {
              ...data,
              chatSession: {
                ...chatSession,
                updated: new Date().toISOString(),
                messages: {
                  ...chatSession.messages,
                  edges: compact([
                    ...chatSession.messages.edges,
                    {
                      __typename: "ChatMessageEdge",
                      node: updatedMessageNode,
                    },
                  ]),
                },
              },
            };
            return updatedData;
          }
        );
      }
    },
    []
  );

  usePusher(channelName, "chat_message_delta", handleChatMessageDelta);

  return null;
}
